import { MarkerType } from 'reactflow';

// __simplici_wire_colour__: the edge's stroke and its arrowhead are set INLINE
// on every edge object, which beats any stylesheet. #b3b3b3 was the grey the
// builder shipped; the wire now takes its colour from --wire (the neon-green
// the flow-builder design calls for), declared per theme on
// .OnboardingFlow--container in views/onboarding-flow/builder-panel-ledger.scss.
// The arrowhead is an SVG marker rendered outside that subtree, where var()
// does not resolve, so it carries the dark theme's literal value.
const WIRE_STROKE = 'var(--wire, #39ffb0)';
const WIRE_ARROW = '#39ffb0';


const BASIC_INFO = 'basicInfo';
const AUTHENTICATION = 'authentication';

const makeEdge = (source: string, target: string) => ({
	id: `${source}->${target}`,
	source,
	target,
	type: 'buttonedge',
	markerEnd: {
		type: MarkerType.Arrow,
		width: 10,
		height: 10,
		color: WIRE_ARROW,
	},
	style: { strokeWidth: 2.5, stroke: WIRE_STROKE, zIndex: 999 },
});

/**
 * Re-hydrate a tier-0 "Basic Info" node onto the canvas.
 *
 * Legacy tiered pipelines were hand-inserted into the DB with a `basicInfo`
 * node that lives only in the pipeline's top-level `nodes[]`, never in
 * `design.nodes`. That makes it invisible in every `design.nodes`-driven
 * renderer (builder canvas + both flow previews) even though the top-level
 * graph is well-formed.
 *
 * The one rule that matters: PRESERVE THE REAL TOPOLOGY. In the authoritative
 * `nodes[]` graph, Basic Info already has a real place — commonly it is the
 * ROOT (`parentId: []`) and Authentication is its child (`auth.parentId` =
 * [basicInfo]). `design.nodes` even carries that `auth.parentId` reference; it
 * is only missing the Basic Info node itself. So we re-insert Basic Info with
 * its TRUE `parentId` from `nodes[]` and touch nobody else's `parentId` —
 * synthesizing a chain here would instead rewrite `auth`'s parent and create a
 *   auth -> basicInfo  AND  basicInfo -> auth
 * cycle with no root, which renders fine but fails the backend's single-root
 * `isGraphValid` on publish ("Disconnected Graph").
 *
 * Only when Basic Info is GENUINELY orphaned in the DB graph (no parent and
 * nothing references it) do we synthesize a chain:
 *   Authentication -> Basic Info -> (former children of Authentication),
 * or, with no Authentication node, Basic Info (root) -> (former roots).
 *
 * The tiered runtime ignores graph edges entirely (it resolves steps by
 * stepId), so this only affects visualization + what the builder persists on
 * the next save. No-op when Basic Info is already on the canvas, or absent from
 * `dbNodes`.
 *
 * @param nodes   design/canvas nodes (each with { id, stepId, parentId, ... })
 * @param edges   design/canvas edges (each with { source, target, ... })
 * @param dbNodes the pipeline's top-level nodes[] (each with { _id, stepId })
 */
export const reconcileBasicInfoGraph = (
	nodes: any[] = [],
	edges: any[] = [],
	dbNodes: any[] = []
): { nodes: any[]; edges: any[] } => {
	const outNodes = (nodes ?? []).map((node: any) => ({
		...node,
		parentId: [...(node?.parentId ?? [])],
	}));
	const outEdges = (edges ?? []).map((edge: any) => ({ ...edge }));

	const dropEdge = (source: string, target: string) => {
		for (let i = outEdges.length - 1; i >= 0; i--) {
			if (outEdges[i]?.source === source && outEdges[i]?.target === target) {
				outEdges.splice(i, 1);
			}
		}
	};
	const addEdgeOnce = (source: string, target: string) => {
		if (!source || !target) return;
		if (
			outEdges.some((edge: any) => edge?.source === source && edge?.target === target)
		) {
			return;
		}
		outEdges.push(makeEdge(source, target));
	};

	// `dbNodes` arrives in one of TWO id spaces and the canvas is only ever in one
	// of them:
	//   - pipeline nodes[]        -> `_id` IS the design/canvas id.
	//   - complex-session-nodes[] -> `_id` is a per-session copy; the design id
	//                                lives on `pipelineNodeId`, and `parentId`
	//                                points at OTHER session-node `_id`s.
	// Comparing a session `_id` against the canvas therefore never matches, so
	// Basic Info was re-injected even when design.nodes already carried it — as an
	// orphan, since its parent id was a session id no canvas node has. Dagre laid
	// that orphan out as its own component at top-left and pushed the real chain
	// out of the list thumbnail's viewport: every session row rendered exactly one
	// "Basic Info" chevron. Resolve to design space first, for both shapes.
	const toDesignId = (node: any) => node?.pipelineNodeId ?? node?._id ?? node?.id;
	const designIdOf = new Map<string, any>();
	for (const node of dbNodes ?? []) {
		const raw = node?._id ?? node?.id;
		if (raw != null) designIdOf.set(String(raw), toDesignId(node));
	}

	const basicInfoDbNodes = (dbNodes ?? []).filter(
		(node: any) => node?.stepId === BASIC_INFO
	);

	for (const dbBasic of basicInfoDbNodes) {
		const basicId = toDesignId(dbBasic);
		if (!basicId) continue;
		// Already on the canvas (new pipelines carry it in design.nodes) — skip.
		if (outNodes.some((node: any) => node.id === basicId)) continue;

		// Real topology from the authoritative top-level nodes[] + canvas graph:
		//  - Basic Info's true parent(s) (empty => it is the graph root), and
		//  - any canvas node that already points at Basic Info as its parent.
		// Parents come from the same id space as the node itself, so they need the
		// same translation before they can be matched against the canvas.
		const dbParents = ((dbBasic?.parentId ?? []) as any[])
			.filter(Boolean)
			.map((p: any) => designIdOf.get(String(p)) ?? p);
		const existingChildren = outNodes.filter(
			(node: any) => (node.parentId ?? []).includes(basicId) && node.id !== basicId
		);
		const hasRealTopology = dbParents.length > 0 || existingChildren.length > 0;

		const authNode = outNodes.find((node: any) => node.stepId === AUTHENTICATION);

		let parentOfBasic: string[];
		// Nodes whose parentId we REWRITE — only ever the synthesized (orphaned)
		// branch. When the DB graph already places Basic Info, we rewrite nobody.
		let childrenToReparent: any[];
		let refPos: { x?: number; y?: number } | undefined;

		if (hasRealTopology) {
			// Preserve the DB graph exactly: keep Basic Info's real parent(s), and
			// the nodes that already reference it stay its children. We only add the
			// missing node + its edges — no parentId is rewritten (avoids the
			// auth<->basicInfo cycle that broke publish).
			parentOfBasic = [...dbParents];
			childrenToReparent = [];
			const parentNode = dbParents.length
				? outNodes.find((n: any) => n.id === dbParents[0])
				: undefined;
			refPos = parentNode?.position ?? existingChildren[0]?.position;
		} else if (authNode) {
			// Orphaned Basic Info, Authentication present: synthesize the chain
			// Authentication -> Basic Info -> (Authentication's children).
			parentOfBasic = [authNode.id];
			childrenToReparent = outNodes.filter(
				(node: any) =>
					(node.parentId ?? []).includes(authNode.id) && node.id !== basicId
			);
			refPos = authNode.position;
		} else {
			// Orphaned Basic Info, no Authentication: Basic Info becomes the root.
			parentOfBasic = [];
			childrenToReparent = outNodes.filter(
				(node: any) => !(node?.parentId?.length) && node.id !== basicId
			);
			refPos = childrenToReparent[0]?.position;
		}

		const basePos = refPos ?? { x: 0, y: 0 };
		const basicNode = {
			id: basicId,
			// `_id` mirrors `id` so node-trimming paths that key on `_id` keep it.
			_id: basicId,
			type: authNode?.type ?? 'customNode',
			position: {
				// Downstream of a parent => place to the right; a root => to the left.
				x: (basePos.x ?? 0) + (parentOfBasic.length ? 220 : -220),
				y: basePos.y ?? 0,
			},
			key: BASIC_INFO,
			stepId: BASIC_INFO,
			label: 'Basic Info',
			data: { label: BASIC_INFO },
			parentId: parentOfBasic,
			conditions: dbBasic?.conditions ?? [],
			zIndex: 99999,
		};
		// Insert near its logical slot (right after its parent, else just before
		// its first child, else at the front) so array-order node trims (e.g. the
		// list thumbnail's first-N slice) don't drop it.
		const anchorId = parentOfBasic[0] ?? existingChildren[0]?.id;
		const anchorIdx = anchorId
			? outNodes.findIndex((n: any) => n.id === anchorId)
			: -1;
		if (anchorIdx >= 0) {
			outNodes.splice(anchorIdx + (parentOfBasic.length ? 1 : 0), 0, basicNode);
		} else {
			outNodes.unshift(basicNode);
		}

		// Edges: parent(s) -> Basic Info, and Basic Info -> its existing children.
		for (const p of parentOfBasic) addEdgeOnce(p, basicId);
		for (const child of existingChildren) addEdgeOnce(basicId, child.id);

		// Only the synthesized (orphaned) branch rewrites existing parentIds.
		for (const child of childrenToReparent) {
			if (authNode) {
				child.parentId = [
					basicId,
					...(child.parentId ?? []).filter((p: string) => p !== authNode.id),
				];
				dropEdge(authNode.id, child.id);
			} else {
				child.parentId = [basicId];
			}
			addEdgeOnce(basicId, child.id);
		}
	}

	return { nodes: outNodes, edges: outEdges };
};
